import { messages } from './messages';
import { CollectionType, type ProductSelectOption } from '../../types';
import type { MessageDescriptor, useIntl } from 'react-intl-next';
import { type GroupType } from '@atlaskit/select';

export type CollectionGroupType<T> = GroupType<T> & {
	tooltip?: string;
};

const DEFAULT_LABEL = messages.defaultGroupLabel;
const groupLabels: Record<
	string,
	(productKey?: string) => {
		label: MessageDescriptor;
		tooltip?: MessageDescriptor;
	}
> = {
	[CollectionType.TEAMWORK_COLLECTION]: (productKey) => {
		let tooltip: MessageDescriptor | undefined;
		if (productKey === 'confluence') {
			tooltip = messages.teamworkCollectionGroupTooltipConfluence;
		} else if (productKey === 'jira-software') {
			tooltip = messages.teamworkCollectionGroupTooltipJira;
		}
		return {
			label: messages.teamworkCollectionGroupLabel,
			tooltip,
		};
	},
};

export const getGroupedProductOptions = (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	productOptions: ProductSelectOption[],
	productKey: string | undefined,
): (ProductSelectOption | CollectionGroupType<ProductSelectOption>)[] => {
	const ungrouped = [];
	const grouped = new Map();

	for (const option of productOptions) {
		if (option.group) {
			const group = grouped.get(option.group) || [];
			group.push(option);
			grouped.set(option.group, group);
		} else {
			ungrouped.push(option);
		}
	}

	const groups = Array.from(grouped.entries());
	if (!groups.length) {
		return ungrouped;
	}

	const options = groups.map(([key, options]) => {
		const copy = groupLabels[key]?.(productKey);

		if (!copy) {
			return { label: key, options };
		}

		const label = formatMessage(copy?.label);
		const tooltip = copy?.tooltip ? formatMessage(copy.tooltip) : undefined;

		return { label, tooltip, options };
	});

	if (ungrouped.length) {
		options.push({ label: formatMessage(DEFAULT_LABEL), options: ungrouped });
	}

	return options;
};
